import { useCallback, useEffect, useState } from "react"
import { listStudentAssessments } from "../../../../api/studentQuestions"
import type { PaginationMeta } from "../../Questions/types"
import type {
  AssessmentCounts,
  AssessmentRecord,
  AssessorFilterTab,
  AssessorSortOrder,
} from "../types"

const PAGE_SIZE = 10
const EMPTY_COUNTS: AssessmentCounts = { total: 0, questions: 0, papers: 0 }

const useAssessmentFeed = () => {
  const [records, setRecords] = useState<AssessmentRecord[]>([])
  const [subjects, setSubjects] = useState<string[]>([])
  const [topics, setTopics] = useState<string[]>([])
  const [summary, setSummary] = useState<AssessmentCounts>(EMPTY_COUNTS)
  const [filtered, setFiltered] = useState<AssessmentCounts>(EMPTY_COUNTS)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilterTab] = useState<AssessorFilterTab>("all")
  const [sort, setSortOrder] = useState<AssessorSortOrder>("newest")
  const [subject, setSubjectValue] = useState("")
  const [topic, setTopicValue] = useState("")
  const [page, setPage] = useState(1)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listStudentAssessments({
        kind: filter,
        subject: subject || undefined,
        topic: topic || undefined,
        sort,
        page_number: page,
        per_page: PAGE_SIZE,
      })
      const data = res.data?.data
      if (res.data?.success && data && Array.isArray(data.records)) {
        setRecords(data.records)
        setSubjects(data.subjects || [])
        setTopics(data.topics || [])
        setSummary(data.summary || EMPTY_COUNTS)
        setFiltered(data.filtered || EMPTY_COUNTS)
        setPagination(res.data.pagination ?? null)
      } else {
        setRecords([])
        setError(res.data?.message || "Could not load assessments")
      }
    } catch {
      setRecords([])
      setError("Could not load assessments")
    } finally {
      setLoading(false)
    }
  }, [filter, subject, topic, sort, page])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const onFocus = () => {
      void refresh()
    }
    window.addEventListener("focus", onFocus)
    return () => window.removeEventListener("focus", onFocus)
  }, [refresh])

  const setFilter = useCallback((value: AssessorFilterTab) => {
    setFilterTab(value)
    setPage(1)
  }, [])

  const setSort = useCallback((value: AssessorSortOrder) => {
    setSortOrder(value)
    setPage(1)
  }, [])

  const setSubject = useCallback((value: string) => {
    setSubjectValue(value)
    setTopicValue("")
    setPage(1)
  }, [])

  const setTopic = useCallback((value: string) => {
    setTopicValue(value)
    setPage(1)
  }, [])

  return {
    loading,
    error,
    records,
    subjects,
    topics,
    summary,
    filtered,
    pagination,
    filter,
    sort,
    subject,
    topic,
    setFilter,
    setSort,
    setSubject,
    setTopic,
    setPage,
    refresh,
  }
}

export default useAssessmentFeed
